<template>
  <div
    class="fee-concession-report"
    :class="{
      'fee-concession-report--class-wise': classWise,
      'fee-concession-report--portrait': isPortraitLayout && !classWise,
    }"
  >
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading fees concession records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div
      v-else
      class="report-page"
      :class="{ 'report-page--portrait': isPortraitLayout }"
    >
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="campusBranch" class="text-muted"> - {{ campusBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ reportTitle }}</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <div class="filter-info-line mt-1" v-if="filterSummary">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="reportItems.length && !classWise && !hideAmountColumn"
        class="summary-section mb-4"
      >
        <div class="summary-card">
          <span class="label">Total Students</span>
          <span class="value">{{ reportItems.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Total Amount</span>
          <span class="value">{{ formatAmount(totalAmount) }}</span>
        </div>
      </div>

      <div v-if="!classWise" class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 4%">#</th>
              <th class="text-center" style="width: 8%">Roll</th>
              <th class="text-left" style="width: 16%">Student Name</th>
              <th class="text-center" style="width: 10%">Class</th>
              <th class="text-center" style="width: 10%">Admission Date</th>
              <th class="text-center" style="width: 10%">Category</th>
              <th class="text-center" style="width: 12%">Fee Status</th>
              <th
                v-if="!hideAmountColumn"
                class="text-right"
                style="width: 10%"
              >
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-center font-weight-bold">{{ item.sno || index + 1 }}</td>
              <td class="text-center roll-cell">{{ item.roll || '-' }}</td>
              <td class="text-left font-weight-bold">{{ (item.name || '-').toUpperCase() }}</td>
              <td class="text-center">{{ item.className || '-' }}</td>
              <td class="text-center">{{ formatDate(item.admissionDate) }}</td>
              <td class="text-center">{{ item.category || '-' }}</td>
              <td class="text-center">{{ item.feeStatus || '-' }}</td>
              <td
                v-if="!hideAmountColumn"
                class="text-right font-weight-bold"
              >
                {{ formatAmount(item.amount) }}
              </td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td
                :colspan="hideAmountColumn ? 7 : 8"
                class="text-center py-5 no-data"
              >
                <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
                <p>No concession records found for the selected criteria</p>
              </td>
            </tr>
            <tr v-if="reportItems.length && !hideAmountColumn" class="totals-row">
              <td colspan="7" class="text-right font-weight-bold">Totals</td>
              <td class="text-right font-weight-bold">{{ formatAmount(totalAmount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="class-wise-section">
        <div
          v-if="!classGroups.length"
          class="text-center py-5 no-data"
        >
          <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
          <p>No concession records found for the selected criteria</p>
        </div>

        <div
          v-for="(group, groupIndex) in classGroups"
          :key="`class-${group.className}-${groupIndex}`"
          class="class-block"
        >
          <div class="class-block-header">
            Class: {{ group.className }}
          </div>

          <table class="ledger-style-table class-wise-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 5%">#</th>
                <th class="text-center" style="width: 10%">Roll</th>
                <th class="text-left" style="width: 28%">Student Name</th>
                <th class="text-center" style="width: 14%">Admission Date</th>
                <th class="text-center" style="width: 14%">Category</th>
                <th class="text-center" style="width: 14%">Fee Status</th>
                <th class="text-right" style="width: 15%">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in group.items"
                :key="`${group.className}-${item.sno || index}`"
              >
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center roll-cell">{{ item.roll || '-' }}</td>
                <td class="text-left font-weight-bold">{{ (item.name || '-').toUpperCase() }}</td>
                <td class="text-center">{{ formatDate(item.admissionDate) }}</td>
                <td class="text-center">{{ item.category || '-' }}</td>
                <td class="text-center">{{ item.feeStatus || '-' }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(item.amount) }}</td>
              </tr>
              <tr class="class-totals-row">
                <td colspan="6" class="text-right font-weight-bold">
                  Class Total ({{ group.className }})
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(groupTotal(group.items)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <table
          v-if="classGroups.length"
          class="ledger-style-table class-wise-table grand-total-table"
        >
          <tbody>
            <tr class="grand-total-row">
              <td colspan="6" class="text-right font-weight-bold">Grand Total</td>
              <td class="text-right font-weight-bold">{{ formatAmount(totalAmount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'FeeConcessionPrint',
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: 'MY SKOOL',
      campusBranch: '',
      schoolLogo: null,
      db: '',
      cID: 0,
      classID: 0,
      sectionID: 0,
      discountReferenceID: 0,
      discountRefName: 'All',
      className: '',
      sectionName: '',
      classWise: false,
      orientation: 'landscape',
      reportDomain: (this.$store.state.report || '').replace(/_/g, ''),
    }
  },
  computed: {
    isPortraitLayout() {
      return this.classWise || this.orientation === 'portrait'
    },
    hideAmountColumn() {
      return !this.classWise && this.orientation === 'portrait'
    },
    reportTitle() {
      return this.classWise
        ? 'Class Wise Fees Concession Report'
        : 'Fees Concession Report'
    },
    classGroups() {
      if (!this.classWise || !this.reportItems.length) return []

      const map = new Map()
      this.reportItems.forEach((item) => {
        const className =
          String(item.className || 'Unassigned').trim() || 'Unassigned'
        if (!map.has(className)) {
          map.set(className, { className, items: [] })
        }
        map.get(className).items.push(item)
      })

      return Array.from(map.values()).sort((a, b) =>
        a.className.localeCompare(b.className, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      )
    },
    currentDateTime() {
      const now = new Date()
      return now.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    totalAmount() {
      return this.reportItems.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0
      )
    },
    filterSummary() {
      const parts = []
      if (this.className) parts.push(`Class: ${this.className}`)
      else if (this.classID && this.classID != 0) parts.push(`Class ID: ${this.classID}`)
      else parts.push('Class: All')

      if (this.sectionName) parts.push(`Section: ${this.sectionName}`)
      else if (this.sectionID && this.sectionID != 0) parts.push(`Section ID: ${this.sectionID}`)
      else parts.push('Section: All')

      parts.push(`Discount Reference: ${this.discountRefName || 'All'}`)
      return parts.join(' | ')
    },
  },
  created() {
    this.initReport()
  },
  beforeDestroy() {
    this.clearPrintPageOrientation()
  },
  methods: {
    initReport() {
      if (this.reportDomain !== 'myskoolaba') {
        this.error = 'This report is only available for Myskool ABA.'
        return
      }

      const query = this.$route.query
      this.db = query.db || this.$store.state.userData.db
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0)
      this.classID = Number(query.classID || 0)
      this.sectionID = Number(query.sectionID || 0)
      this.discountReferenceID = Number(query.discountReferenceID || 0)
      this.discountRefName = query.discountRefName || 'All'
      this.className = query.className || ''
      this.sectionName = query.sectionName || ''
      this.classWise =
        query.classWise === '1' ||
        query.classWise === 1 ||
        query.classWise === 'true'
      this.orientation =
        String(query.orientation || 'landscape').toLowerCase() === 'portrait'
          ? 'portrait'
          : 'landscape'

      if (!this.db) {
        this.error = 'Database identifier is missing. Please close and try again.'
        return
      }

      this.setPrintPageOrientation()
      this.loadCampusInfo()
      this.loadReportData()
    },
    setPrintPageOrientation() {
      this.clearPrintPageOrientation()
      const style = document.createElement('style')
      style.id = 'fee-concession-print-orientation'
      style.textContent = `@media print { @page { size: A4 ${
        this.isPortraitLayout ? 'portrait' : 'landscape'
      }; margin: 8mm 6mm; } }`
      document.head.appendChild(style)
    },
    clearPrintPageOrientation() {
      document.getElementById('fee-concession-print-orientation')?.remove()
    },
    async loadCampusInfo() {
      try {
        const url = 'https://apiv26.myskool.app/api/campuses'
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (
          response.data &&
          response.data.status === 'success' &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID)
          if (match) {
            this.campusName = match.name
            this.campusBranch = this.cID == 0 ? 'All Campuses' : match.branch
            this.schoolLogo = match.logo
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || []
        const currentBranch =
          branches.find((b) => b.id == this.cID) || this.$store.state.currentBranch
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName || currentBranch.branch || 'MY SKOOL'
          this.campusBranch = currentBranch.branch || ''
        }
      }
    },
    async loadReportData() {
      this.loading = true
      this.error = null

      try {
        const url = `${this.$store.state.domain}Fees/FeeConcessionReport`
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            classID: this.classID || 0,
            sectionID: this.sectionID || 0,
            discountReferenceID: this.discountReferenceID || 0,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (response.data && response.data.status === 'success') {
          this.reportItems = response.data.data || []
        } else {
          this.error =
            response.data?.message ||
            'The server returned an unexpected response format.'
        }
      } catch (err) {
        console.error('Error fetching fees concession report:', err)
        this.error =
          'Failed to retrieve report data. Please ensure the API endpoint is accessible.'
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    formatAmount(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return '-'
      return num.toLocaleString('en-PK')
    },
    groupTotal(items) {
      return (items || []).reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0
      )
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.fee-concession-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: 'Outfit', 'Inter', sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &--portrait {
    max-width: 820px;
  }
}

.fee-concession-report--portrait {
  .report-header-modern {
    .school-logo-img {
      height: 90px;
    }

    .school-name-full {
      font-size: 1.5rem;
    }
  }

  .ledger-style-table {
    th,
    td {
      padding: 8px 6px;
      font-size: 0.82rem;
    }
  }
}

.fee-concession-report--class-wise {
  .class-wise-section {
    display: block;
  }

  .class-block {
    margin-bottom: 0.75rem;
  }

  .class-block-header {
    padding: 0.35rem 0;
    margin-bottom: 0.25rem;
    font-size: 0.95rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    border-bottom: 2px solid #333;
  }

  .class-wise-table {
    margin-top: 0;
  }

  .class-totals-row {
    background: #f8f9fa !important;
    border-top: 1px solid #333;
  }

  .grand-total-table {
    margin-top: 0.5rem;
  }

  .grand-total-row {
    background: #f8f9fa !important;
    border-top: 2px solid #333;
  }

  .roll-cell {
    color: #000;
    font-weight: 700;
  }
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }

  .filter-info-line {
    font-size: 0.95rem;
    color: #636e72;
    font-weight: 600;
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 10px;
    box-sizing: border-box;
    font-size: 0.9rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even):not(.totals-row) {
    background-color: #f8f9fa;
  }

  tbody tr:hover:not(.totals-row):not(.class-totals-row):not(.grand-total-row) {
    background: #f8f9fa;
  }

  .roll-cell {
    font-weight: 700;
    color: #333;
  }

  .totals-row {
    background: #f8f9fa !important;
    border-top: 2px solid #333;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .fee-concession-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 8pt !important;
    width: 100% !important;

    th,
    td {
      padding: 5px 6px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 12px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #000 !important;

    .school-logo-img {
      height: 70px !important;
      max-width: 180px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.15rem !important;
    }

    .report-info-line {
      font-size: 9pt !important;
    }

    .filter-info-line {
      font-size: 8pt !important;
    }
  }

  .summary-section {
    display: none !important;
  }

  .fee-concession-report--portrait:not(.fee-concession-report--class-wise) {
    .report-header-modern {
      margin-bottom: 8px !important;
      padding-bottom: 6px !important;

      .school-logo-img {
        height: 55px !important;
        max-width: 140px !important;
      }

      .school-name-full {
        font-size: 11pt !important;
      }

      .report-info-line {
        font-size: 8pt !important;
      }

      .filter-info-line {
        font-size: 7pt !important;
      }
    }

    .report-page--portrait {
      max-width: 100% !important;
      padding: 0.35cm 0.25cm !important;
    }

    .ledger-style-table {
      font-size: 7.5pt !important;
      margin-top: 0 !important;

      th,
      td {
        padding: 3px 4px !important;
        line-height: 1.2 !important;
      }
    }

    .ledger-style-table tbody tr:nth-child(even):not(.totals-row) {
      background: #fff !important;
    }
  }

  .fee-concession-report--class-wise {
    .report-header-modern {
      margin-bottom: 6px !important;
      padding-bottom: 4px !important;

      .school-logo-img {
        height: 50px !important;
        max-width: 120px !important;
      }

      .school-name-full {
        font-size: 10pt !important;
      }

      .report-info-line {
        font-size: 8pt !important;
      }

      .filter-info-line {
        font-size: 7pt !important;
      }
    }

    .report-page--portrait {
      max-width: 100% !important;
      padding: 0.25cm 0.2cm !important;
    }

    .class-wise-section {
      display: block !important;
    }

    .class-block {
      page-break-inside: auto !important;
      break-inside: auto !important;
      margin-bottom: 4px !important;
    }

    .class-block-header {
      background: transparent !important;
      border: none !important;
      border-bottom: 1px solid #000 !important;
      border-radius: 0 !important;
      padding: 2px 0 1px !important;
      margin-bottom: 2px !important;
      font-size: 8pt !important;
      color: #000 !important;
      page-break-after: avoid !important;
      break-after: avoid !important;
    }

    .class-totals-row,
    .grand-total-row {
      background: #fff !important;
      border-top: 1px solid #000 !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    .class-wise-table {
      font-size: 7pt !important;
      margin-top: 0 !important;
      page-break-inside: auto !important;
      break-inside: auto !important;

      thead {
        display: table-header-group !important;
      }

      tbody tr:not(.class-totals-row) {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
    }

    .grand-total-table {
      margin-top: 2px !important;
    }

    .ledger-style-table {
      margin-top: 0 !important;

      th,
      td {
        padding: 2px 4px !important;
        line-height: 1.2 !important;
      }
    }

    .ledger-style-table tbody tr:nth-child(even):not(.class-totals-row) {
      background: #fff !important;
    }

    .roll-cell {
      color: #000 !important;
    }
  }
}
</style>
